import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import "./CohortCard.scss";
import { useDispatch, useSelector } from "react-redux";
import exportimages from "../../../../assets/images/exportImages";
import { DeleteCohortModel } from "./DeleteCohortModel";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSubscription } from "../../../../hooks/useSubscription";
import { PRIVILEGES } from "../../../../utils/privileges";
import { EDIT_MODEL, EditCohortModel } from "./EditCohortModel";
import {
  editCohortSlice,
  setIsEditCohort,
} from "../../../../rtk-query/features/cohort/cohortSlice";
import { Dropdown } from "react-bootstrap";

const ACTIVE_BADGE_STYLE = {
  backgroundColor: "#81A3F5",
  color: "#ffff",
};

const INACTIVE_BADGE_STYLE = {
  backgroundColor: "#ffffff00",
  color: "#667085",
};

const DROPDOWN_MENU_STYLE = {
  marginTop: "0.5rem",
  borderRadius: "6px 0 6px 6px",
};

const stopEvent = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const CohortCard = ({
  uid,
  title,
  subtitle,
  range,
  institutions,
  avg,
  percentile,
  enableOptions,
  isDefault,
}) => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const { hasSubscription } = useSubscription();
  const [showDeleteCohortModel, setShowDeleteCohortModel] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showEditCohortModel, setShowEditCohortModel] = useState(false);
  const [switchEditModel, setSwitchEditModel] = useState(EDIT_MODEL.EDIT);

  const selectedCohort = useSelector((state) => state?.cohortReducer?.selectedCohort);

  const isActive = uid === selectedCohort?.uid;
  const canEditCohort =
    hasPermission(PRIVILEGES.EDIT_COHORT) && hasSubscription(PRIVILEGES.EDIT_COHORT);
  const canDeleteCohort =
    hasPermission(PRIVILEGES.DELETE_COHORT) && hasSubscription(PRIVILEGES.DELETE_COHORT);
  const shouldShowOptions = canEditCohort && enableOptions;
  const shouldShowCohortActions = canDeleteCohort;
  const defaultActionLabel = isDefault ? "Remove as default" : "Mark as default";
  const badgeStyle = isActive ? ACTIVE_BADGE_STYLE : INACTIVE_BADGE_STYLE;
  const handleShowDropdown = () => setShowDropdown(true);
  const handleHideDropdown = () => setShowDropdown(false);

  useEffect(() => {
    if (showEditCohortModel) {
      setSwitchEditModel(EDIT_MODEL.EDIT);
    }
  }, [showEditCohortModel]);

  useEffect(() => {
    const currentDocument = globalThis?.document;

    if (!showDropdown || !currentDocument) {
      return undefined;
    }

    const handleDocumentClick = () => setShowDropdown(false);

    currentDocument.addEventListener("click", handleDocumentClick);

    return () => {
      currentDocument.removeEventListener("click", handleDocumentClick);
    };
  }, [showDropdown]);

  const handleEditCohortList = (e) => {
    stopEvent(e);
    dispatch(setIsEditCohort(true));
  };

  const handleEditCohortDetails = (e) => {
    stopEvent(e);
    dispatch(setIsEditCohort(true));
    setShowEditCohortModel(true);
  };

  const handleToggleDefault = (e) => {
    stopEvent(e);
    dispatch(editCohortSlice({ cohortTitle: title, cohortDesc: subtitle, uid, isDefault }));
  };

  const handleDeleteCohort = (e) => {
    stopEvent(e);
    setShowDeleteCohortModel(true);
  };

  const handleDeleteCohortHide = () => setShowDeleteCohortModel(false);
  const handleEditCohortHide = () => setShowEditCohortModel(false);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={`cohort-card cursor-pointer${isActive ? " active" : ""}`}
        onMouseLeave={handleHideDropdown}
      >
        <div className="top-section">
          {isDefault && (
            <div className="badge" style={badgeStyle}>
              Default
            </div>
          )}
          <h4>{title}</h4>
          <p>{subtitle}</p>
          <div className="range">{range}</div>

          {shouldShowOptions && (
            <Dropdown show={showDropdown}>
              <button
                type="button"
                className={`edit-icon${isActive ? " show" : ""}`}
                id="dropdown-basic"
                onMouseEnter={handleShowDropdown}
                onFocus={handleShowDropdown}
                onBlur={handleHideDropdown}
                onClick={stopEvent}
              >
                <img
                  alt="icon"
                  src={
                    isActive
                      ? exportimages.list_icon_edit_solid_white
                      : exportimages.list_icon_edit_solid
                  }
                />
              </button>
              <Dropdown.Menu style={DROPDOWN_MENU_STYLE}>
                {shouldShowCohortActions && (
                  <>
                    <Dropdown.Item onClick={handleEditCohortList}>
                      Edit Cohort List
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleEditCohortDetails}>
                      Edit Cohort Details
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleToggleDefault}>
                      {defaultActionLabel}
                    </Dropdown.Item>
                  </>
                )}
                {shouldShowCohortActions && (
                  <Dropdown.Item onClick={handleDeleteCohort}>
                    Delete Cohort
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
        <div className="bottom-section">
          <div className="stats">
            <div>
              <span className="stat-label">Institutions</span>
              <span className="stat-value">{institutions}</span>
            </div>
            <div>
              <span className="stat-label">Cohort Average</span>
              <span className="stat-value">{avg}</span>
            </div>
            <div>
              <span className="stat-label">Percentile</span>
              <span className="stat-value">{percentile}</span>
            </div>
          </div>
        </div>
      </div>

      <DeleteCohortModel
        show={showDeleteCohortModel}
        onHide={handleDeleteCohortHide}
        uid={uid}
      />
      <EditCohortModel
        show={showEditCohortModel}
        onHide={handleEditCohortHide}
        uid={uid}
        title={title}
        subtitle={subtitle}
        switchEditModel={switchEditModel}
        setSwitchEditModel={setSwitchEditModel}
      />
    </>
  );
};

CohortCard.propTypes = {
  uid: PropTypes.string.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  range: PropTypes.string,
  institutions: PropTypes.number,
  avg: PropTypes.string,
  percentile: PropTypes.string,
  enableOptions: PropTypes.bool,
  isDefault: PropTypes.bool,
};

CohortCard.defaultProps = {
  title: "",
  subtitle: "",
  range: "",
  institutions: 0,
  avg: "",
  percentile: "",
  enableOptions: false,
  isDefault: false,
};

export default CohortCard;
